import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Play, Lock, User } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useVideoMembershipAccess } from '@/hooks/useVideoMembershipAccess';
import { useNavigate } from 'react-router-dom';

const VideoHoopsRecaps: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { hasAccess, requiredMembership, loading } = useVideoMembershipAccess('video_hoops_recaps');

  const handleActionClick = () => {
    console.log('🎯 VideoHoopsRecaps - handleActionClick triggered');
    console.log('🎯 User:', user ? 'logged in' : 'not logged in');
    console.log('🎯 Has Access:', hasAccess);
    console.log('🎯 Loading:', loading);
    console.log('🎯 Required Membership:', requiredMembership);

    if (!user) {
      console.log('🎯 Redirecting to /register with return URL');
      // Add return URL so user comes back to video page after registering
      navigate('/register?returnTo=/videos/hoops_recaps');
      return;
    }

    if (hasAccess) {
      console.log('🎯 Redirecting to /videos/hoops_recaps/watch');
      // Redirect to video watching page
      navigate('/videos/hoops_recaps/watch');
      return;
    }

    console.log('🎯 Redirecting to /register for purchase');
    // Redirect to register page for purchase
    navigate('/register?returnTo=/videos/hoops_recaps');
  };

  const getActionButton = () => {
    if (loading) {
      return (
        <Button disabled className="w-full">
          Checking Access...
        </Button>
      );
    }

    if (!user) {
      return (
        <Button onClick={handleActionClick} className="w-full">
          <User className="h-4 w-4 mr-2" />
          Register to Purchase
        </Button>
      );
    }

    if (hasAccess) {
      return (
        <Button onClick={handleActionClick} className="w-full">
          <Play className="h-4 w-4 mr-2" />
          Watch Videos
        </Button>
      );
    }

    return (
      <Button onClick={handleActionClick} className="w-full">
        <Lock className="h-4 w-4 mr-2" />
        Purchase Access
      </Button>
    );
  };

  const formatPrice = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-primary text-primary-foreground py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold mb-4">Hoops Recaps</h1>
          <p className="text-xl opacity-90 max-w-2xl mx-auto">
            Exclusive basketball game recaps and highlights from top tournaments and events
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <Card className="overflow-hidden">
            <div className="aspect-video relative">
              <img
                src="/placeholder.svg"
                alt="Hoops Recaps Preview"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                <Play className="h-16 w-16 text-white" />
              </div>
              {!hasAccess && (
                <div className="absolute top-4 right-4">
                  <Badge variant="secondary" className="bg-red-500 text-white">
                    <Lock className="h-3 w-3 mr-1" />
                    Premium Content
                  </Badge>
                </div>
              )}
            </div>
            
            <CardHeader>
              <CardTitle className="text-2xl">Basketball Game Recaps</CardTitle>
              <div className="flex items-center justify-between">
                {requiredMembership && (
                  <div className="flex items-center text-primary font-semibold text-lg">
                    {formatPrice(requiredMembership.price_cents)}
                  </div>
                )}
                <Badge variant="outline">
                  Premium Content
                </Badge>
              </div>
            </CardHeader>
            
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                Get exclusive access to professionally produced basketball game recaps and highlights. 
                Our content includes detailed breakdowns, key plays, and expert analysis from top-tier games.
              </p>
              
              <div className="space-y-2">
                <h4 className="font-semibold">What's Included:</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Full game recap videos</li>
                  <li>• Key highlight reels</li>
                  <li>• Expert game analysis</li>
                  <li>• Player performance breakdowns</li>
                  <li>• Exclusive behind-the-scenes content</li>
                </ul>
              </div>

              {hasAccess && (
                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                  <div className="flex items-center text-green-700 dark:text-green-400">
                    <Play className="h-4 w-4 mr-2" />
                    <span className="font-medium">You have access to this content!</span>
                  </div>
                </div>
              )}
              
              {getActionButton()}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default VideoHoopsRecaps;